import { useEffect, useRef } from 'react';

export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-20 md:py-28 bg-cream-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          ref={ref}
          className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 items-center transition-all duration-700 ease-out opacity-0 translate-y-8"
        >
          {/* Left: Image */}
          <div className="lg:col-span-2 relative">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/1fe80d3942e240cc0fc4dc83f7a64a11.jpg"
                alt="Sweet Celebrations bakery kitchen"
                className="w-full h-[480px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/30 to-transparent" />
            </div>
            {/* Gold badge */}
            <div className="absolute -bottom-6 -right-4 md:-right-6 bg-gold-500 text-white rounded-2xl p-5 shadow-lg">
              <p className="font-serif text-3xl font-bold">2007</p>
              <p className="text-xs font-sans uppercase tracking-widest mt-1">Est.</p>
            </div>
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-3 flex flex-col gap-6">
            <div className="flex items-center gap-3">
              <div className="h-px w-10 bg-gold-500" />
              <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Our Story</span>
            </div>

            <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 leading-tight">
              Baked with Heart,<br />
              <em className="italic text-blush-500">Loved by Lubbock</em>
            </h2>

            <div className="flex flex-col gap-5 text-warm-gray text-base leading-[1.9] font-sans font-light">
              <p>
                Sweet Celebrations began in 2007 with a simple love for beautiful, delicious cakes. What started as heartfelt creations quickly grew into something far greater — a beloved bakery celebrated for its creativity, craftsmanship, and the joy it brought to every celebration it touched.
              </p>
              <p>
                After earning a reputation as one of Abilene, Texas's most sought-after custom bakeries, Sweet Celebrations made the exciting move to Lubbock — and the community embraced us with open arms. Our doors opened to neighbors, families, couples, and dreamers who all shared one thing: a desire for something truly special.
              </p>
              <p>
                Over the years, we expanded beyond custom orders to offer cake decorating classes and baking supplies, sharing our passion for the craft with the next generation of bakers right here in the Hub City.
              </p>
            </div>

            {/* Values */}
            <div className="grid grid-cols-2 gap-4 mt-2">
              {[
                { icon: 'ri-award-line', label: 'Award-Winning Quality' },
                { icon: 'ri-heart-line', label: 'Made with Love' },
                { icon: 'ri-lightbulb-line', label: 'Creative Customization' },
                { icon: 'ri-map-pin-line', label: 'Proudly Local' },
              ].map((v) => (
                <div key={v.label} className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blush-100 text-blush-500">
                    <i className={`${v.icon} text-base`} />
                  </div>
                  <span className="text-charcoal-800 text-sm font-sans font-semibold">{v.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
